import {Component, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import {MatStepper} from '@angular/material/stepper';
import {User} from '../../../dtos/user';
import {UserService} from '../../../services/user/user.service';
import {MatDialogRef} from '@angular/material/dialog';
import {NotificationService} from '../../../services/common/notification.service';
import {ValidatorService} from '../../../services/common/validator.service';

const passwordValidator: ValidatorFn = (fg: UntypedFormGroup) => {
  const pw = fg.get('pw1').value;
  const pw2 = fg.get('pw2').value;
  if (pw !== null && pw2 !== null) {
    if (pw !== pw2) {
      fg.get('pw2').setErrors({mismatching: true});
      return {mismatching: true};
    }
  }
  if (pw !== null && pw2 !== null) {
    if (pw === pw2) {
      fg.get('pw2').setErrors({mismatching: null});
      fg.get('pw2').updateValueAndValidity({onlySelf: true, emitEvent: false});
      return null;
    }
  }
  return null;
};

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    standalone: false
})
export class RegistrationComponent implements OnInit {

  @ViewChild('stepper') stepper: MatStepper;

  // Form Groups that are displayed in the stepper
  loginInformationForm: UntypedFormGroup;
  personalInformationForm: UntypedFormGroup;

  // Variables to show/hide an entered password
  hidePw: boolean;
  hidePw2: boolean;

  // Variables to check whether the forms were submitted or not (to decide whether or not to show field validation)
  loginSubmitted: boolean;
  personalSubmitted: boolean;

  // Initial ID used to autofocus
  private targetId = 'input0';

  constructor(private _formBuilder: UntypedFormBuilder, private userService: UserService, private notificationService: NotificationService,
              private dialogRef: MatDialogRef<RegistrationComponent>, private validatorService: ValidatorService) {
  }

  ngOnInit(): void {
    // Initially hide passwords
    this.hidePw = true;
    this.hidePw2 = true;
    this.loginInformationForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      pw1: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(255)]],
      pw2: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(255)]]
    }, {
      validators: [passwordValidator] // Custom validator: Verification of equivalency
    });
    this.personalInformationForm = this._formBuilder.group({
      firstName: ['', Validators.maxLength(255)],
      lastName: ['', Validators.maxLength(255)],
      country: ['', Validators.maxLength(255)],
      city: ['', Validators.maxLength(255)],
      zip: ['', this.validatorService.zipCodeValidator],
      street: ['', Validators.maxLength(255)]
    });
  }

  // Move to stepper with index id
  move(id: number) {
    if (this.stepper.selectedIndex === 0) {
      this.loginSubmitted = true;
      this.loginInformationForm.markAllAsTouched();
      if (this.loginInformationForm.valid) {
        this.stepper.selectedIndex = id;
      }
    } else {
      this.stepper.selectedIndex = id;
    }
  }

  /*
  setFocus() sets focus on an input element
  setTargetId extracts the id of the element which is to be focussed
   */
  setFocus() {
    const targetElem = document.getElementById(this.targetId);
    targetElem.focus();
  }

  // TODO check with Max
  setTargetId(event: any) {
    this.targetId = `input${event.selectedIndex}`;
  }

  submit() {
    this.personalSubmitted = true;
    if (this.loginInformationForm.valid && this.personalInformationForm.valid) {
      const user = new User(
        null,
        this.personalInformationForm.controls.lastName?.value.trim(),
        this.personalInformationForm.controls.firstName?.value.trim(),
        this.loginInformationForm.controls.email?.value.trim(),
        this.loginInformationForm.controls.pw1?.value.trim(),
        this.personalInformationForm.controls.street?.value.trim(),
        this.personalInformationForm.controls.zip?.value,
        this.personalInformationForm.controls.country?.value.trim(),
        this.personalInformationForm.controls.city?.value.trim(),
        null,
        null
      );
      this.userService.register(user).subscribe(() => {
        this.notificationService.openSnackBar('Account wurde erfolgreich erstellt.');
      }, error => {
        this.notificationService.openSnackBar(error);
      });
      this.dialogRef.close();
    }
  }
}
